import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/AdminLayout';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Download, Filter, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

export default function AdminHistorico() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    fecha_desde: '',
    fecha_hasta: '',
    zona: '',
    estado: '',
    tipo_enseres: ''
  });

  const fetchHistorico = async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, per_page: 50, ...filters };
      // Remove empty filters
      Object.keys(params).forEach(key => !params[key] && delete params[key]);
      
      const resp = await axios.get(`${API}/admin/historico`, { headers: authHeaders(), params });
      setSolicitudes(resp.data.solicitudes);
      setTotal(resp.data.total);
      setPages(resp.data.pages);
      setPage(pg);
    } catch (err) {
      toast.error('Error al cargar histórico');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHistorico(1); }, []);

  const handleExport = async (format) => {
    try {
      const params = { ...filters };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const url = `${API}/admin/historico/export.${format}`;
      const resp = await axios.get(url, {
        headers: authHeaders(),
        params,
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      const from = filters.fecha_desde || 'all';
      const to = filters.fecha_hasta || 'all';
      a.download = `historico_${from}_${to}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Exportación ${format.toUpperCase()} descargada`);
    } catch (err) {
      toast.error(`Error al exportar ${format.toUpperCase()}`);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-semibold text-[#374B67]">Histórico de Solicitudes</h1>
            <p className="text-sm text-slate-500">{total} registros total</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => handleExport('xlsx')} variant="outline" size="sm" data-testid="historico-export-xlsx">
              <Download size={14} className="mr-2" />
              Exportar Excel
            </Button>
            <Button onClick={() => handleExport('pdf')} variant="outline" size="sm" data-testid="historico-export-pdf">
              <Download size={14} className="mr-2" />
              Exportar PDF
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Desde</Label>
              <Input
                type="date"
                value={filters.fecha_desde}
                onChange={(e) => setFilters({ ...filters, fecha_desde: e.target.value })}
                data-testid="historico-filter-desde"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Hasta</Label>
              <Input
                type="date"
                value={filters.fecha_hasta}
                onChange={(e) => setFilters({ ...filters, fecha_hasta: e.target.value })}
                data-testid="historico-filter-hasta"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Zona</Label>
              <Input
                placeholder="Filtrar por zona"
                value={filters.zona}
                onChange={(e) => setFilters({ ...filters, zona: e.target.value })}
                data-testid="historico-filter-zona"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Estado</Label>
              <Select value={filters.estado} onValueChange={(val) => setFilters({ ...filters, estado: val })}>
                <SelectTrigger data-testid="historico-filter-estado">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Todos</SelectItem>
                  <SelectItem value="Notificada">Notificada</SelectItem>
                  <SelectItem value="Asignada">Asignada</SelectItem>
                  <SelectItem value="En Ruta">En Ruta</SelectItem>
                  <SelectItem value="Recogida">Recogida</SelectItem>
                  <SelectItem value="Completada">Completada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Tipo Enseres</Label>
              <Input
                placeholder="Ej: Muebles"
                value={filters.tipo_enseres}
                onChange={(e) => setFilters({ ...filters, tipo_enseres: e.target.value })}
                data-testid="historico-filter-tipo"
              />
            </div>
          </div>
          <Button onClick={() => fetchHistorico(1)} data-testid="historico-apply-filters">
            <Filter size={16} className="mr-2" />
            Aplicar Filtros
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando...</div>
        ) : solicitudes.length === 0 ? (
          <div className="text-center py-12 text-slate-500">No hay datos</div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Ticket</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Fecha</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Ciudadano</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Zona</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Tipo</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {solicitudes.map((sol) => (
                      <tr key={sol.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="px-4 py-3 font-mono text-xs">{sol.ticket_code}</td>
                        <td className="px-4 py-3 text-xs">{sol.fecha_peticion || sol.created_at?.split('T')[0]}</td>
                        <td className="px-4 py-3">{sol.nombre_completo}</td>
                        <td className="px-4 py-3 text-xs">{sol.zona || 'N/A'}</td>
                        <td className="px-4 py-3 text-xs">{sol.tipo_enseres || 'N/A'}</td>
                        <td className="px-4 py-3"><StatusBadge estado={sol.estado} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">Página {page} de {pages}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => fetchHistorico(page - 1)} disabled={page === 1}>
                    <ChevronLeft size={16} /> Anterior
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => fetchHistorico(page + 1)} disabled={page === pages}>
                    Siguiente <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
